import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useRouter } from "next/router";

import unionBy from "lodash/unionBy";
import isEqual from "lodash/isEqual";
import Modal from "components/modals/Modal";
import differenceBy from "lodash/differenceBy";
import SizeSelect from "components/SizeSelect";
import LocationSelector from "components/LocationSelector";
import MarketLocationSelector from "components/MarketLocationSelector";

import { legacyHandlers, uuidHandlers } from "../areaChangeHandlers";
import styles from "./style.module.scss";

const ListingSearchForm = ({
  state,
  subMarket,
  numEmployees,
  neighborhoods, // selected neighborhoods from url
  submarkets, // selected submarkets from url
  selectedMarket,
  isOOM,
  city,
}) => {
  const router = useRouter();
  const [showLocationSelector, setShowLocationSelector] = useState(false);

  // this state is used to optimistically update the UI rather than wait for data query to get new neighborhoods from props. This results in a snappier UX
  const [selectedNeighborhoods, setSelectedNeighborhoods] =
    useState(neighborhoods);
  const [selectedSubmarkets, setSelectedSubmarkets] = useState(submarkets);

  const SelectedNeighborhoodLength = useRef(neighborhoods.length);
  const SelectedSubmarketLength = useRef(submarkets.length);
  useEffect(() => {
    // reset neighborhoods. This will only be called if query in getServerSideProps fails.
    if (SelectedNeighborhoodLength !== neighborhoods.length) {
      setSelectedNeighborhoods(neighborhoods);
    }
    if (SelectedSubmarketLength !== submarkets.length) {
      setSelectedSubmarkets(submarkets);
    }
  }, [neighborhoods, submarkets]);

  const toggleLocationSelector = (resetState = true) => {
    if (resetState) {
      // reset state when location selector is closed without changes
      setSelectedNeighborhoods(neighborhoods);
      setSelectedSubmarkets(submarkets);
    }
    setShowLocationSelector(!showLocationSelector);
  };

  const updateLocation = () => {
    const hasNeighborhoodsChanged = !isEqual(
      selectedNeighborhoods.map(({ id }) => id),
      neighborhoods.map(({ id }) => id)
    );
    const hasSubmarketsChanged = !isEqual(
      selectedSubmarkets.map(({ id }) => id),
      submarkets.map(({ id }) => id)
    );
    if (selectedMarket && (hasSubmarketsChanged || hasNeighborhoodsChanged)) {
      if (isOOM)
        legacyHandlers.onNeighborhoodChange(
          selectedNeighborhoods,
          selectedSubmarkets,
          router
        );
      else {
        uuidHandlers.onLocationChange(
          [...selectedNeighborhoods, ...selectedSubmarkets],
          selectedMarket,
          router
        );
      }
    }
    toggleLocationSelector(false);
  };

  const titleizedNhoods = selectedNeighborhoods.map((n) => n.name);
  const titleizedSubmarkets = selectedSubmarkets.map((n) => n.name);
  const titleizedAreas = [...titleizedNhoods, ...titleizedSubmarkets];

  const onNeighborhoodChange = (neighborhood, checked) => {
    if (checked) {
      const newNeighborhoods = [...selectedNeighborhoods, neighborhood];
      setSelectedNeighborhoods(newNeighborhoods);
    } else {
      const neighborhoodsSubmarket = selectedSubmarkets.filter(
        ({ neighborhoods: nhoods }) =>
          nhoods.some((nhood) => nhood.id === neighborhood.id)
      );
      if (neighborhoodsSubmarket.length > 0) {
        // if code gets here neighborhoodsSubmarket will always be 1
        const newNeighborhoods = neighborhoodsSubmarket[0].neighborhoods
          .filter((n) => n.id !== neighborhood.id)
          .filter(({ visibility }) => visibility === "VISIBLE");
        setSelectedNeighborhoods([
          ...selectedNeighborhoods,
          ...newNeighborhoods,
        ]);
        setSelectedSubmarkets(
          differenceBy(selectedSubmarkets, neighborhoodsSubmarket, "id")
        );
      } else {
        const newNeighborhoods = selectedNeighborhoods.filter((n) =>
          n.id
            ? n.id !== neighborhood.id
            : n.name !== neighborhood.parameterizedName
        );
        setSelectedNeighborhoods(newNeighborhoods);
      }
    }
  };

  const onSubmarketStateChange = (sMarket, checked) => {
    const arrayHandlerMethod = checked ? unionBy : differenceBy;
    const newNeighborhoods = differenceBy(
      selectedNeighborhoods,
      sMarket.neighborhoods,
      "id"
    );
    const newSubmarkets = arrayHandlerMethod(
      selectedSubmarkets,
      [sMarket],
      "id"
    );
    setSelectedNeighborhoods(newNeighborhoods);
    setSelectedSubmarkets(newSubmarkets);
  };

  // event handler for legacy location selector
  const onChangeLocation = (payload) => {
    if (payload.city) {
      legacyHandlers.onCityChange(payload, router);
    } else {
      legacyHandlers.onNeighborhoodChange(payload.neighborhoods, [], router);
    }
  };

  const onSizeChange = (payload) => {
    const { query } = router;
    if (!payload.numEmployees) {
      delete query.numEmployees;
    } else {
      query.numEmployees = payload.numEmployees;
    }
    delete query.slug;

    router.push({
      pathname: router.asPath.split("?")[0],
      query,
    });
  };

  return (
    <form action="/search" className={styles.form}>
      <div className={styles.formRow}>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor="location">Location</label>
        <div
          className={`select ${styles.select}`}
          onClick={() => toggleLocationSelector(true)}
          onKeyPress={() => {}}
          role="menu"
          tabIndex="0"
        >
          {selectedMarket?.name || subMarket}
          {titleizedAreas.length > 0 && ` - ${titleizedAreas.join(", ")}`}
        </div>
        {showLocationSelector && (
          <Modal
            onClose={() => toggleLocationSelector(true)}
            className={styles.modal}
          >
            <h1>Select a location</h1>
            {selectedMarket ? (
              <MarketLocationSelector
                showMarketSelector={false}
                selectedMarket={selectedMarket}
                onSubmarketChange={onSubmarketStateChange}
                selectedNeighborhoods={selectedNeighborhoods}
                selectedSubmarkets={selectedSubmarkets}
                selectorsClassName={styles.selectorsClassName}
                onNeighborhoodChange={onNeighborhoodChange}
                accessor="submarkets"
              />
            ) : (
              <LocationSelector
                state={state}
                city={city}
                onChange={onChangeLocation}
                className={styles.locationSelector}
                neighborhoods={selectedNeighborhoods}
              />
            )}
            <button
              type="button"
              className={`btn-primary btn-wide ${styles.okButton}`}
              onClick={updateLocation}
              data-qa="listing-search-form-ok-btn"
            >
              OK
            </button>
          </Modal>
        )}
      </div>
      <div className={styles.formRow}>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor="numEmployees">Size</label>
        <SizeSelect
          name="numEmployees"
          onChange={onSizeChange}
          value={numEmployees}
          className={styles.select}
        />
      </div>
    </form>
  );
};

ListingSearchForm.propTypes = {
  state: PropTypes.string,
  numEmployees: PropTypes.string,
  subMarket: PropTypes.string.isRequired,
  selectedMarket: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    position: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
  }),
  neighborhoods: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      geojson: PropTypes.shape({
        type: PropTypes.string,
        coordinates: PropTypes.arrayOf(
          PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))
        ),
      }),
      yearlyPricePerFoot: PropTypes.number,
      parameterizedName: PropTypes.string,
    })
  ),
  submarkets: PropTypes.arrayOf(PropTypes.shape({})),
  isOOM: PropTypes.bool,
  city: PropTypes.string,
};

ListingSearchForm.defaultProps = {
  state: null,
  numEmployees: "",
  neighborhoods: [],
  submarkets: [],
  isOOM: false,
  selectedMarket: null,
  city: null,
};

export default ListingSearchForm;
